import { memo, useEffect } from 'react';

import { PlayIcon, ArrowLeftIcon } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_TRYIT } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import { Button } from '@/components/ui/button';
import { ShortcutHint } from '@/components/ui/kbd';
import { SHORTCUTS } from '@/constants/shortcuts';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import useDatasources from '@/hooks/useDatasources';
import { useFromPageState } from '@/hooks/useFromPageState';
import { useSetDefaultSidebar } from '@/hooks/useSetDefaultSidebar';
import { useSidebar } from '@/hooks/useSidebar';
import { openPublishModal } from '@/redux/collections/actions';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setBrainId, setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectBrainId, selectAccountSlug } from '@/redux/session/selectors';
import { setDefaultSidebar } from '@/redux/sidebar/actions';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { ContentBreadcrumbs } from './breadcrumbs/ContentBreadcrumbs';
import {
  RIGHT_PANEL_SIDEBAR_KINDS,
  RightPanelContent,
} from '../components/RightPanel/RightPanelContent';
import { ContentTable } from '../components/Tables/ContentTable/ContentTable';
import { useResetSidebarSelection } from '../hooks/useResetSidebarSelection';
import { useRTMCollection } from '../hooks/useRTMCollection';
import { DeleteDocumentModal } from '../modals/DeleteDocumentModal';
import { PublishModal } from '../modals/PublishModal';

const CONTENT_PAGE_SIDEBARS = ['datasource_details', 'ingestion_report'] as const;
type ContentPageSidebar = (typeof CONTENT_PAGE_SIDEBARS)[number];

export const ContentPage = memo(() => {
  useSegments(PageName.DATASOURCE);
  const pageIndex = useFromPageState();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const brainId = useSelector(selectBrainId);
  const { brains } = useBrains();

  const { category, collectionId, datasourceId } = useParams();
  const { collection } = useCollections(collectionId);
  const { datasource } = useDatasources(collectionId, datasourceId);
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  const { content } = useSidebar();

  const defaultSidebar: ContentPageSidebar =
    category === 'webpages' ? 'ingestion_report' : 'datasource_details';

  const testTooltipContent = (
    <span className="inline-flex items-center gap-1.5">
      {t('collections.test_tooltip')} <ShortcutHint shortcut={SHORTCUTS.test} />
    </span>
  );

  const publishTooltipContent = (
    <span className="inline-flex items-center gap-1.5">
      {t('collections.publish_tooltip')} <ShortcutHint shortcut={SHORTCUTS.publish} />
    </span>
  );

  useTitle(t('pages.kb_content', { 0: datasource?.name ?? '-', 1: collection?.name ?? '-' }));

  useResetSidebarSelection();

  const openTryItModal = () => {
    // This is the brain id that will be used to open the try it modal
    // First try to use the current brain if it matches the collection language
    // Then try to find a brain that matches the collection language
    // Finally fall back to null if no language match is found
    const initialBrainId =
      (brainId &&
        brains.find(
          (brain) => brain.brain_id === brainId && brain.language === collection?.language
        )?.brain_id) ||
      brains.find((brain) => brain.language === collection?.language)?.brain_id ||
      null;

    dispatch(setBrainId({ brainId: initialBrainId }));

    dispatch(
      pushModal(MODAL_TRYIT, { brainId: initialBrainId, collectionLanguage: collection?.language })
    );
  };

  useHotkeys(
    SHORTCUTS.test,
    (e: KeyboardEvent) => {
      e.preventDefault();
      openTryItModal();
    },
    { enableOnFormTags: false },
    [brains, brainId, collection?.language]
  );

  useHotkeys(
    SHORTCUTS.publish,
    (e: KeyboardEvent) => {
      e.preventDefault();
      if (!canWrite) {
        return;
      }
      dispatch(openPublishModal());
    },
    { enableOnFormTags: false },
    [canWrite]
  );

  useSetDefaultSidebar(defaultSidebar);

  // On context change (not panel change) drop an inherited panel and restore the default.
  useEffect(() => {
    if (content && !CONTENT_PAGE_SIDEBARS.some((panel) => panel === content)) {
      dispatch(setDefaultSidebar(defaultSidebar));
      dispatch(setDatasourceId(null));
      dispatch(setDocumentId(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, datasourceId, defaultSidebar, dispatch]);

  useRTMCollection(collectionId);

  return (
    <div className="h-full w-full">
      <SidebarLayout
        autoSaveId="collections-sidebar"
        sidebar={
          <Sidebar title={t('common.details')} when={RIGHT_PANEL_SIDEBAR_KINDS}>
            <RightPanelContent />
          </Sidebar>
        }
      >
        <PageSubHeader
          breadcrumbs={
            <PageSubHeaderBreadcrumbs>
              <div className="flex items-center">
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  onClick={() =>
                    navigate(`/${slug}/knowledge-bases/${collectionId}/category/${category}`, {
                      state: { fromPage: pageIndex + 1 },
                    })
                  }
                >
                  <ArrowLeftIcon size={16} />
                </Button>
                <div className="mr-4 ml-2 h-4 w-px bg-border" />
                <ContentBreadcrumbs />
              </div>
            </PageSubHeaderBreadcrumbs>
          }
          actions={
            <PageSubHeaderActions>
              <div className="flex items-center gap-2">
                <InfoTooltip content={testTooltipContent} size="large">
                  <Button
                    variant="outline-neutral"
                    size="sm"
                    onClick={openTryItModal}
                    className="flex items-center"
                  >
                    <PlayIcon className="h-4 w-4" />
                    {t('common.test')}
                  </Button>
                </InfoTooltip>

                <InfoTooltip content={publishTooltipContent}>
                  <div>
                    <Button
                      size="sm"
                      disabled={!canWrite}
                      onClick={() => dispatch(openPublishModal())}
                    >
                      {t('collections.publish')}
                    </Button>
                  </div>
                </InfoTooltip>
              </div>
            </PageSubHeaderActions>
          }
        />

        <div className={TABLE_LAYOUT_FILL_CLASSES}>
          <ContentTable />
        </div>
      </SidebarLayout>

      <DeleteDocumentModal />

      <PublishModal />
    </div>
  );
});
ContentPage.displayName = 'ResizableContent';
